import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { clsx } from 'clsx';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react';
import {
  SearchBoxCore,
  SearchBoxOptions,
  SearchBoxRetrieveResponse,
  SearchBoxSuggestion,
  SessionToken
} from '@mapbox/search-js-core';

import ActionsCancelNormalXsmall from '@strava/icons/ActionsCancelNormalXsmall';
import NavigationSearchNormalXsmall from '@strava/icons/NavigationSearchNormalXsmall';
import {
  color as colorTokens,
  typography as typographyTokens,
  utilities as utilityTokens
} from '@strava/ui-tokens/js';

import { Spinner } from '../../Spinner/Spinner';
import {
  languageMapToIETFTag,
  normalizedSearchQuery,
  searchBoxCustomSearch
} from '../GeoSearchBox/GeoSearchBox.utils';
import { getAccessToken } from '../utils';
import {
  CoordinateRetrieveResponse,
  handleCoordinateSelection as handleCoordinateSelectionUtil,
  handleRegularSelection as handleRegularSelectionUtil,
  isCoordinateSuggestion
} from './GeoSearchBoxCustom.utils';

import styles from './GeoSearchBoxCustom.module.scss';

// Re-export types for consumers
export type { SearchBoxRetrieveResponse } from '@mapbox/search-js-core';

export type {
  CoordinateRetrieveResponse,
  CoordinateSuggestion
} from './GeoSearchBoxCustom.utils';

export interface GeoSearchBoxCustomTheme {
  variables?: {
    fontFamily?: string;
    fontWeight?: string;
    borderRadius?: string;
    colorText?: string;
    lineHeight?: string;
    padding?: string;
    [key: string]: string | undefined;
  };
  cssText?: string;
}

export interface GeoSearchBoxCustomProps {
  /**
   * Mapbox API token for the Search API
   */
  accessToken?: string;

  /**
   * Options to pass to the underlying SearchBoxCore interface.
   * Note: For optimal performance, this object should be memoized to avoid
   * unnecessary re-initialization of SearchBoxCore.
   */
  options?: Partial<SearchBoxOptions>;

  /**
   * The input element's placeholder text
   */
  placeholder?: string;

  /**
   * Value to display in the search box
   */
  value?: string;

  /**
   * Callback for when the value changes
   */
  onChange?: (value: string) => void;

  /**
   * Fired when the user is typing and provides suggestions
   */
  onSuggest?: (res: {
    suggestions: SearchBoxSuggestion[];
    attribution?: string;
  }) => void;

  /**
   * Fired when SearchBoxCore has errored
   */
  onSuggestError?: (error: Error) => void;

  /**
   * Fired when the user has selected a suggestion.
   * For coordinate searches, a simplified response is returned.
   */
  onRetrieve?: (
    res: SearchBoxRetrieveResponse | CoordinateRetrieveResponse
  ) => void;

  /**
   * Fired when the geocoder is looking up a query
   */
  onLoading?: (e: { query: string }) => void;

  /**
   * Fired when the user has cleared the search box. If the component rendering the GeoSearchBoxCustom
   * manages a marker, don't forget to remove it here, e.g.:
   *  if (markerRef.current) {
   *    markerRef.current.remove();
   *    markerRef.current = null;
   *  }
   */
  onClear?: () => void;

  /**
   * Theme customization
   */
  theme?: GeoSearchBoxCustomTheme;

  /**
   * Intercept search to validate/manipulate input.
   * Defaults to normalizedSearchQuery for coordinate normalization.
   */
  interceptSearch?: (value: string) => string;

  /**
   * CSS class name
   */
  className?: string;
}

export interface GeoSearchBoxCustomRefType {
  focus: () => void;
  search: (text: string) => void;
}

export const GeoSearchBoxCustom = forwardRef<
  GeoSearchBoxCustomRefType,
  GeoSearchBoxCustomProps
>(
  (
    {
      accessToken,
      options,
      placeholder = 'Search',
      value = '',
      onChange,
      onSuggest,
      onSuggestError,
      onRetrieve,
      onLoading,
      onClear,
      theme,
      interceptSearch,
      className
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(value);
    const [suggestions, setSuggestions] = useState<SearchBoxSuggestion[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const [attribution, setAttribution] = useState<string>('');

    const inputRef = useRef<HTMLInputElement>(null);
    const searchBoxCoreRef = useRef<SearchBoxCore | null>(null);
    const sessionTokenRef = useRef<SessionToken>(new SessionToken());
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize SearchBoxCore
    useEffect(() => {
      const token = getAccessToken(accessToken);
      if (!token) {
        return;
      }

      // Destructure language to transform it before passing to SearchBoxCore
      const { language, ...restOptions } = options ?? {};

      searchBoxCoreRef.current = new SearchBoxCore({
        accessToken: token,
        language: languageMapToIETFTag(language),
        ...restOptions
      });
    }, [accessToken, options]);

    // Sync external value prop with internal state
    useEffect(() => {
      setInputValue(value);
    }, [value]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        // Clear any pending debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    // Floating UI for dropdown positioning
    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      middleware: [
        offset(4),
        flip(),
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              minWidth: `${rects.reference.width}px`
            });
          }
        })
      ],
      whileElementsMounted: autoUpdate
    });

    // Handle Escape key and outside clicks
    const dismiss = useDismiss(context);

    // Merge interaction handlers
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

    // Reset selectedIndex when dropdown closes
    useEffect(() => {
      if (!isOpen) {
        setSelectedIndex(-1);
      }
    }, [isOpen]);

    /**
     * Performs a search for location suggestions based on user input.
     * First checks if the input is a coordinate pair (e.g., "40.7128, -74.0060").
     * If so, creates local coordinate suggestions. Otherwise, queries the Mapbox
     * Search API for place suggestions. Debounced to 300ms by handleInputChange.
     */
    const performSearch = useCallback(
      async (searchText: string) => {
        if (!searchBoxCoreRef.current) {
          return;
        }

        try {
          setIsLoading(true);
          onLoading?.({ query: searchText });

          // Apply interceptSearch if provided, default to normalizedSearchQuery
          // for coordinate normalization
          const processedQuery = interceptSearch
            ? interceptSearch(searchText)
            : normalizedSearchQuery(searchText);

          if (!processedQuery) {
            setSuggestions([]);
            setIsOpen(false);
            setIsLoading(false);
            return;
          }

          // Check for custom coordinate search first (e.g., "40.7128, -74.0060")
          // Returns local suggestions without calling Mapbox API
          const customSuggestions = await searchBoxCustomSearch(processedQuery);
          if (customSuggestions && customSuggestions.length > 0) {
            setSuggestions(customSuggestions);
            setIsOpen(true);
            setIsLoading(false);
            if (onSuggest) {
              onSuggest({ suggestions: customSuggestions, attribution: '' });
            }
            return;
          }

          // Perform regular search with Mapbox API for place names
          // Destructure language to transform it separately to avoid override
          const { language, ...restOptions } = options ?? {};
          const result = await searchBoxCoreRef.current.suggest(
            processedQuery,
            {
              sessionToken: sessionTokenRef.current,
              language: languageMapToIETFTag(language),
              ...restOptions
            }
          );

          setSuggestions(result.suggestions);
          setAttribution(result.attribution || '');
          setIsOpen(result.suggestions.length > 0);
          setIsLoading(false);

          if (onSuggest) {
            onSuggest(result);
          }
        } catch (error) {
          setIsLoading(false);
          setSuggestions([]);
          setIsOpen(false);
          if (onSuggestError) {
            onSuggestError(error as Error);
          }
        }
      },
      [interceptSearch, options, onSuggest, onSuggestError, onLoading]
    );

    // Expose ref methods (placed after performSearch to use it as dependency)
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          inputRef.current?.focus();
        },
        search: (text: string) => {
          setInputValue(text);
          performSearch(text);
        }
      }),
      [performSearch]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setSelectedIndex(-1);

      if (onChange) {
        onChange(newValue);
      }

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!newValue.trim()) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      // Debounce search by 300ms to avoid excessive API calls
      debounceTimerRef.current = setTimeout(() => {
        performSearch(newValue);
      }, 300);
    };

    const handleSuggestionSelect = async (suggestion: SearchBoxSuggestion) => {
      // Coordinate suggestions have custom _geometry property and don't need API retrieve
      if (isCoordinateSuggestion(suggestion)) {
        handleCoordinateSelectionUtil(suggestion, {
          setInputValue,
          setIsOpen,
          onChange,
          onRetrieve
        });
        return;
      }

      // Regular Mapbox suggestions need full retrieve() call for complete details
      await handleRegularSelectionUtil(suggestion, {
        searchBoxCoreRef,
        sessionTokenRef,
        options,
        setInputValue,
        setIsOpen,
        onChange,
        onRetrieve
      });
    };

    const handleClear = () => {
      setInputValue('');
      setSuggestions([]);
      setIsOpen(false);
      setSelectedIndex(-1);
      setAttribution('');

      if (onChange) {
        onChange('');
      }

      if (onClear) {
        onClear();
      }

      // Defer focus to ensure state updates are flushed first
      // This prevents the onFocus handler from seeing stale suggestions state
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && suggestions[selectedIndex]) {
            handleSuggestionSelect(suggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    };

    // Apply theme variables as inline styles
    const themeStyles: React.CSSProperties = {
      fontFamily:
        theme?.variables?.fontFamily || typographyTokens.fontFamilyBoathouse,
      fontWeight:
        theme?.variables?.fontWeight || typographyTokens.fontWeightBook,
      borderRadius:
        theme?.variables?.borderRadius || utilityTokens.borderRadiusSm,
      color: theme?.variables?.colorText || colorTokens.colorExtendedNeutralN2,
      lineHeight:
        theme?.variables?.lineHeight || typographyTokens.lineHeightScaleLg,
      ...(theme?.variables?.padding && { padding: theme.variables.padding })
    };

    return (
      <div
        className={clsx(styles.searchBox, className)}
        data-testid="geo-search-box"
      >
        <div
          ref={refs.setReference}
          className={styles.inputWrapper}
          style={themeStyles}
          data-testid="geo-search-box-input-wrapper"
          {...getReferenceProps()}
        >
          <NavigationSearchNormalXsmall className={styles.searchIcon} />

          {/* Input Field */}
          <input
            ref={inputRef}
            name="geo-search-box-input"
            type="text"
            className={styles.input}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) {
                setIsOpen(true);
              }
            }}
            style={themeStyles}
          />

          {inputValue && (
            <>
              {isLoading ? (
                <div className={styles.spinnerWrapper}>
                  <Spinner size={16} />
                </div>
              ) : (
                <button
                  className={styles.clearBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  aria-label="Clear search"
                >
                  <ActionsCancelNormalXsmall aria-hidden="true" />
                </button>
              )}
            </>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {isOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={styles.dropdown}
              {...getFloatingProps()}
            >
              {isLoading ? (
                <div className={styles.loadingState}>Loading...</div>
              ) : (
                <>
                  <ul className={styles.suggestionList}>
                    {suggestions.map((suggestion, index) => (
                      <li
                        key={suggestion.mapbox_id || index}
                        className={clsx(styles.suggestionItem, {
                          [styles.selected]: index === selectedIndex
                        })}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        data-testid={`suggestion-item-${index}`}
                      >
                        <div className={styles.suggestionContent}>
                          <div className={styles.suggestionName}>
                            {suggestion.name}
                          </div>
                          {suggestion.place_formatted && (
                            <div className={styles.suggestionAddress}>
                              {suggestion.place_formatted}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* Attribution */}
                  {attribution && (
                    <div className={styles.attribution}>
                      <a
                        href="https://www.mapbox.com/search-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        title={attribution}
                      >
                        Powered by Mapbox
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </FloatingPortal>
        )}

        {/* Custom CSS from theme */}
        {theme?.cssText && (
          <style data-testid="geo-search-box-custom-css">{theme.cssText}</style>
        )}
      </div>
    );
  }
);

GeoSearchBoxCustom.displayName = 'GeoSearchBoxCustom';
